import React, { useCallback, useMemo, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import Form from '@atlaskit/form';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalTitle,
	ModalBody,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { MODES } from '../../../common/constants';
import type { FormData } from '../../../common/types';
import { getTruncatedTitleForFlag } from '../../../common/utils';
import { useIsAdding, useIsUpdating } from '../../../controllers/store/index.tsx';
import {
	externalResourcesAddUpdateExperience,
	markAddUpdateExperienceFailed,
	ProductExperienceStart,
} from '../../../experiences';
import { AddLinkForm } from './add-link-form';
import { messages } from './messages';
import type { AddLinkModalProps } from './types';

export const AddLinkModal = ({
	isOpen,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onClose: onCloseProp = () => {},
	mode = MODES.ADD,
	link = null,
}: AddLinkModalProps) => {
	const { formatMessage } = useIntl();
	const [isAdding, { addExternalLink }] = useIsAdding();
	const { showFlag } = useFlagService();
	const [isUpdating, { updateExternalLink }] = useIsUpdating();
	const [{ data: pageContextData }] = usePageContext();
	const [isModified, setIsModified] = useState(false);

	const portalUrl = pageContextData?.serviceDeskContext.portalUrl;

	const onClose = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(_: any, analyticsEvent?: UIAnalyticsEvent) => {
			if (analyticsEvent) {
				fireUIAnalytics(
					analyticsEvent,
					mode === MODES.EDIT
						? 'externalResourceUpdateModalClosed'
						: 'externalResourceAddModalClosed',
				);
			}
			onCloseProp();
			externalResourcesAddUpdateExperience.abort();
			setIsModified(false);
		},
		[mode, onCloseProp, setIsModified],
	);

	const onSubmit = useCallback(
		async (formData: FormData): Promise<undefined | Partial<FormData>> => {
			if (mode === MODES.ADD) {
				try {
					await addExternalLink(formData);

					showFlag({
						type: 'success',
						isAutoDismiss: true,
						title: messages.externalLinkAddSuccessFlagTitle,
						description: [
							messages.externalLinkAddSuccessFlagDescription,
							{
								linkTitle: getTruncatedTitleForFlag(formData.title),
							},
						],
						actions: [
							{
								content: messages.linkFlagActionLabel,
								href: portalUrl,
								target: '_blank',
							},
						],
					});

					// @ts-expect-error - TS2554 - Expected 1-2 arguments, but got 0.
					onClose();
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (submitError: any) {
					showFlag({
						type: 'error',
						isAutoDismiss: true,
						title: messages.externalLinkAddFailureTitle,
						description: messages.externalLinkAddFailureDescription,
					});
				}
			} else {
				try {
					if (link !== null) {
						await updateExternalLink(formData, link.id);

						showFlag({
							type: 'success',
							isAutoDismiss: true,
							title: messages.externalLinkUpdateSuccessFlagTitle,
							description: [
								messages.externalLinkUpdateSuccessFlagDescription,
								{
									linkTitle: getTruncatedTitleForFlag(formData.title),
								},
							],
							actions: [
								{
									content: messages.linkFlagActionLabel,
									href: portalUrl,
									target: '_blank',
								},
							],
						});
					}

					// @ts-expect-error - TS2554 - Expected 1-2 arguments, but got 0.
					onClose();
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (submitError: any) {
					showFlag({
						type: 'error',
						isAutoDismiss: true,
						title: messages.externalLinkUpdateFailureTitle,
						description: messages.externalLinkUpdateFailureDescription,
					});
				}
			}
			return undefined;
		},
		[mode, addExternalLink, showFlag, portalUrl, onClose, link, updateExternalLink],
	);

	const modalFooterActions = useMemo(() => {
		// add link modal actions
		if (mode === MODES.ADD) {
			return (
				<>
					<Button
						appearance="subtle"
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'externalResourceAddLinkModalCancel');

							// @ts-expect-error - TS2554 - Expected 1-2 arguments, but got 0.
							onClose();
						}}
						isDisabled={isAdding}
					>
						{formatMessage(messages.externalLinksCancel)}
					</Button>
					<LoadingButton
						appearance="primary"
						type="submit"
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'externalResourceAddLinkModalAdd');
						}}
						isDisabled={isAdding}
						isLoading={isAdding}
					>
						{formatMessage(messages.externalLinksAdd)}
					</LoadingButton>
				</>
			);
		}

		// update link modal actions
		return (
			<>
				<Button
					appearance="subtle"
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'externalResourceUpdateLinkModalCancel');

						// @ts-expect-error - TS2554 - Expected 1-2 arguments, but got 0.
						onClose();
					}}
					isDisabled={isUpdating}
				>
					{formatMessage(messages.externalLinksCancel)}
				</Button>
				<LoadingButton
					type="submit"
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'externalResourceUpdateLinkModalSave');
					}}
					isLoading={isUpdating}
					isDisabled={isUpdating || !isModified}
					appearance="primary"
				>
					{formatMessage(messages.externalLinksSaveLink)}
				</LoadingButton>
			</>
		);
	}, [formatMessage, isAdding, onClose, mode, isUpdating, isModified]);

	const ModalContainer = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(containerProps: any) => (
			<Form {...containerProps} onSubmit={onSubmit}>
				{({ formProps }) => <form {...formProps}>{containerProps.children}</form>}
			</Form>
		),
		[onSubmit],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<>
					<JSErrorBoundary
						id="externalResourcesAdd"
						packageName="jiraServicedeskExternalResourcesSettings"
						teamName="jsm-nebula"
						fallback="flag"
						onError={markAddUpdateExperienceFailed}
					>
						<ShortcutScope>
							<ModalDialog
								testId="servicedesk-external-links.ui.links.add-link-modal.add-links-modal"
								onClose={onClose}
								width="medium"
							>
								<ModalContainer>
									<ModalHeader>
										<ModalTitle>
											{mode === MODES.ADD
												? formatMessage(messages.externalLinksAddLink)
												: formatMessage(messages.externalLinksEditLink)}
										</ModalTitle>
									</ModalHeader>
									<ModalBody>
										<ProductExperienceStart ufoExperience={externalResourcesAddUpdateExperience} />
										<AddLinkForm
											link={link}
											mode={mode}
											isModified={isModified}
											setIsModified={setIsModified}
										/>
									</ModalBody>
									<ModalFooter>{modalFooterActions}</ModalFooter>
								</ModalContainer>
							</ModalDialog>
						</ShortcutScope>
					</JSErrorBoundary>
				</>
			)}
		</ModalTransition>
	);
};
